import React, { useEffect, useCallback, useRef, useMemo, useReducer } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DashboardRow from '@/dashboard/components/DashboardRow/DashboardRow';
import {
  SelectedYearType,
  getMonthNamesFromCountsResponse,
  isValidCountsResponse,
} from '@/dashboard/utils/dashboard.utils';
import { getReadableMonthYear } from '@/payroll-period/utils/payrollPeriod.utils';
import { DashboardCountsProvider } from '@/dashboard/provider/DashboardCountsProvider';
import {
  TimerType,
  getDashboardRefreshTimeByEnv,
  pluralize,
  isAbortError,
} from '@/common/utils/app.utils';
import SectionError from '@/components/Error/SectionError';
import tokens from '@/dashboard/tokens.json';
import {
  dashboardApi,
  GetUnacceptedBatchesResponse,
  isValidGetUnacceptedBatchesResponse,
} from '@/dashboard/services/dashboardApi';
import { useDashboardContext } from '@/dashboard/provider/DashboardContextProvider';
import SectionLoader from '@/components/Loader/SectionLoader';
import GraphLegend from '@/components/GraphLegend/GraphLegend';
import { API_CACHE_KEYS } from '@/core/network/baseAppApi';
import useAlert from '@/hooks/useAlert';

interface DashboardGraphLoaderProps {
  selectedYear: SelectedYearType;
}

function ErrorElement({ children }: { children: React.ReactNode }) {
  return (
    <Typography component="p" role="alert">
      {children}
    </Typography>
  );
}

type JobAlertState = { status: 'idle' | 'success' | 'error'; message: string | null };
type JobAlertAction =
  | { type: 'JOB_SUCCESS'; message: string }
  | { type: 'JOB_ERROR'; message: string }
  | { type: 'RESET' };

const jobAlertReducer = (state: JobAlertState, action: JobAlertAction): JobAlertState => {
  switch (action.type) {
    case 'JOB_SUCCESS':
      return { status: 'success', message: action.message };
    case 'JOB_ERROR':
      return { status: 'error', message: action.message };
    case 'RESET':
      return { status: 'idle', message: null };
    default:
      return state;
  }
};

function DashboardGraphLoader({ selectedYear }: DashboardGraphLoaderProps) {
  const {
    useGetCountsMutation,
    useGetUnacceptedBatchesMutation,
    useGetRunningJobsMutation,
    usePollSubmitMutation,
  } = dashboardApi;
  const {
    schedules,
    setBatchSubmitActions,
    batchSubmitActions: originalBatchSubmitActions,
    appState,
    isPolling: contextIsPolling,
    setIsPolling,
  } = useDashboardContext();
  const [getRunningJobs, { data: runningJob }] = useGetRunningJobsMutation();
  const [pollSubmit] = usePollSubmitMutation();
  const [currentJobStatus, setCurrentJobStatus] = React.useState<string | null>(null);
  const [currentProgress, setCurrentProgress] = React.useState<number>(0);
  const [currentJobError, setCurrentJobError] = React.useState<string | null>(null);
  const [jobAlertState, dispatchJobAlert] = useReducer(jobAlertReducer, {
    status: 'idle',
    message: null,
  });
  const isSubmitting = appState.submit.isSubmitting;
  const submitError = appState.submit.error;
  const submitProgress = appState.submit.progress;

  useAlert({
    mui: {
      severity: 'error',
    },
    custom: {
      description: submitError || '',
    },
    app: {},
    dependencies: [submitError],
    condition: () => submitError != null,
  });

  useAlert({
    mui: {
      severity: 'error',
    },
    custom: {
      description: currentJobError || '',
    },
    app: {},
    dependencies: [currentJobError],
    condition: () => currentJobError != null,
  });

  useAlert({
    mui: {
      severity: jobAlertState.status === 'success' ? 'success' : 'error',
    },
    custom: {
      description: jobAlertState.message || '',
    },
    app: {},
    dependencies: [jobAlertState],
    condition: () => jobAlertState.status !== 'idle' && jobAlertState.message != null,
  });

  const [
    getCountsForDashboard,
    {
      data: countsData,
      isError: countsIsError,
      error: countsError,
      isSuccess: countsIsSuccess,
      isLoading: countsIsLoading,
    },
  ] = useGetCountsMutation({ fixedCacheKey: API_CACHE_KEYS.SUMMARIZE });
  const [
    getUnacceptedBatches,
    {
      data: batchData,
      isError: batchIsError,
      error: batchError,
      isSuccess: batchIsSuccess,
      isLoading: batchIsLoading,
    },
  ] = useGetUnacceptedBatchesMutation();
  const refreshTimer = useRef<TimerType | null>(null);
  const promiseTracker = useRef<{
    counts: ReturnType<typeof getCountsForDashboard>;
    unacceptedBatches: ReturnType<typeof getUnacceptedBatches>;
  } | null>(null);

  const isLoading = countsIsLoading || batchIsLoading;
  const isSuccess = countsIsSuccess && batchIsSuccess;

  useEffect(() => {
    if (countsData && Object.keys(countsData).length > 0) {
      if (countsData && Object.keys(countsData).length > 0) {
        const employerId = countsData[Object.keys(countsData)[0]]?.employerId;
        if (employerId) {
          void getRunningJobs({ employerId });
        }
      }
    } // Trigger the mutation on mount
  }, [countsData, getRunningJobs]);

  useEffect(() => {
    if (currentJobStatus === 'Running') {
      setIsPolling(true);
      dispatchJobAlert({ type: 'RESET' });
      const pollIndefinitely = async () => {
        const INITIAL_POLL_INTERVAL_MS = 2000; // Start at 2s (or adjust)
        let pollInterval = INITIAL_POLL_INTERVAL_MS;
        const startTime = Date.now();
        const TIMEOUT_MS = 20 * 60 * 1000;
        while (true) {
          if (Date.now() - startTime > TIMEOUT_MS) {
            setCurrentJobError('The job timed out after 20 minutes. Please try again.');
            setIsPolling(false);
            break;
          }
          const employerId = countsData?.[Object.keys(countsData)[0]]?.employerId;
          const jobId = runningJob?.jobStatus?.jobId;
          if (employerId && jobId) {
            const result = await pollSubmit({ jobId, employerId });
            if (result.data?.status) {
              setCurrentJobStatus(result.data.status);
              setCurrentProgress(result.data.progress);
              const periods = getMonthNamesFromCountsResponse(countsData || {});
              let periodString = batchData?.unacceptedPeriods?.[0]?.period;
              if (!periodString && countsData && typeof countsData === 'object') {
                const firstKey = Object.keys(countsData)[0];
                periodString = firstKey;
              }
              const currentPeriod = periods.find((p) => p.numeric === periodString);
              const readableMonth =
                currentPeriod?.readable ||
                (periodString ? getReadableMonthYear(periodString) : 'unknown period');
              if (result.data.status === 'Failed') {
                dispatchJobAlert({
                  type: 'JOB_ERROR',
                  message:
                    readableMonth && readableMonth !== 'unknown period'
                      ? tokens['BATCH_SUBMIT_FAILURE'].replace('{n}', readableMonth)
                      : tokens['GENERIC_BATCH_SUBMIT_FAILURE'],
                });
                setIsPolling(false);
                break; // Stop on failure
              } else if (result.data.status === 'Succeeded') {
                dispatchJobAlert({
                  type: 'JOB_SUCCESS',
                  message: tokens['BATCH_SUBMIT_OK'].replace('{n}', readableMonth),
                });
                setIsPolling(false);
                break; // Stop on success
              }
            }
          }
          await new Promise((res) => setTimeout(res, pollInterval));
          const elapsedTime = Date.now() - startTime;
          if (elapsedTime < 20000) {
            // First 20 seconds: stay responsive for fast jobs
            pollInterval = 2000;
          } else if (elapsedTime < 60000) {
            // 20s–60s: moderate polling
            pollInterval = 5000;
          } else {
            // After 1 minute: back off but cap it
            pollInterval = 10000;
          }
        }
      };
      void pollIndefinitely(); // Start indefinite polling
    } else {
      setIsPolling(false);
    }
  }, [runningJob, countsData, currentJobStatus, pollSubmit, setIsPolling]);
  useEffect(() => {
    if (runningJob) {
      setCurrentJobStatus(runningJob.jobStatus.status);
      setCurrentProgress(runningJob.jobStatus.progress);
    }
  }, [runningJob]);

  const getCountsAndUnacceptedBatches = useCallback(() => {
    if (schedules && selectedYear) {
      const periods = schedules[selectedYear];
      if (promiseTracker.current) {
        promiseTracker.current.counts.abort();
        promiseTracker.current.unacceptedBatches.abort();
      }
      const promise = {
        counts: getCountsForDashboard({ periods: periods.months }),
        unacceptedBatches: getUnacceptedBatches(),
      };
      promiseTracker.current = promise;
    }
  }, [selectedYear]);

  const contributionCountByPeriod = useMemo(() => {
    return (
      batchData?.unacceptedPeriods?.map(({ period, contributionCount }) => ({
        [period]: contributionCount,
      })) ?? []
    ).reduce((acc, obj) => {
      for (const key in obj) {
        acc[key] = obj[key];
      }
      return acc;
    }, {});
  }, [batchData]);

  const clearTimer = useCallback(() => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
    }
  }, []);

  const batchSubmitActions = {
    submitStart: originalBatchSubmitActions.submitStart,
    submitEnd: () => {
      originalBatchSubmitActions.submitEnd();
      getCountsAndUnacceptedBatches();
    },
  };

  useEffect(() => {
    getCountsAndUnacceptedBatches();
    setBatchSubmitActions(batchSubmitActions);
    return () => {
      clearTimer();
    };
  }, [selectedYear]);

  const getErrorElement = () => {
    if (selectedYear === null || isLoading) return <SectionLoader />;
    if (countsIsError && !isAbortError<typeof countsError>(countsError))
      return <ErrorElement>{countsError as string}</ErrorElement>;
    if (batchIsError && !isAbortError(batchError))
      return <ErrorElement>{batchError as string}</ErrorElement>;
    return null;
  };

  const getProgressBox = () => {
    return (
      <Dialog
        open={isSubmitting || currentJobStatus === 'Running' || contextIsPolling}
        aria-labelledby="progress-dialog-title"
        disableEscapeKeyDown
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="progress-dialog-title">
          SUBMISSION IN PROGRESS{' '}
          {isSubmitting ? `(${submitProgress || 0}%)` : `(${currentProgress || 0}%)`}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Please wait - this may take up to 10 minutes for larger datasets.
          </Typography>
          <LinearProgress
            variant="determinate"
            value={isSubmitting ? submitProgress || 0 : currentProgress || 0}
          />
        </DialogContent>
      </Dialog>
    );
  };

  const renderContent = () => {
    const errorElement = getErrorElement();
    if (errorElement) return errorElement;

    if (
      isSuccess &&
      countsData &&
      isValidCountsResponse(countsData) &&
      isValidGetUnacceptedBatchesResponse(batchData)
    ) {
      clearTimer();
      refreshTimer.current = setTimeout(
        getCountsAndUnacceptedBatches,
        getDashboardRefreshTimeByEnv(),
      );

      const periods = getMonthNamesFromCountsResponse(countsData);
      const noPeriods = periods.length === 0;

      if (noPeriods) {
        return <SectionError title={tokens['NO_COUNTS']} />;
      }

      const getDashboardContent = () => (
        <DashboardCountsProvider
          counts={countsData}
          unacceptedBatches={batchData as GetUnacceptedBatchesResponse}
          contributionCountByPeriod={contributionCountByPeriod ?? {}}
        >
          {periods.map((period) => (
            <DashboardRow
              key={period.monthId}
              readableMonth={period.readable}
              numericMonth={period.numeric}
            />
          ))}
        </DashboardCountsProvider>
      );
      return (
        <>
          <Box
            component="ol"
            display="flex"
            flexDirection="column"
            gap={1}
            sx={{ overflowY: 'auto' }}
            aria-label={`Total ${periods.length} ${pluralize('period', periods.length)}.`}
          >
            {isSubmitting || currentJobStatus === 'Running' || contextIsPolling
              ? getProgressBox()
              : getDashboardContent()}
          </Box>
          <GraphLegend />
        </>
      );
    }

    if (
      isSuccess &&
      (!isValidCountsResponse(countsData) || !isValidGetUnacceptedBatchesResponse(batchData))
    ) {
      return <ErrorElement>{tokens['GENERAL_ERROR']}</ErrorElement>;
    }

    return <SectionLoader />;
  };

  return renderContent();
}

export default DashboardGraphLoader;
